import EventEmitter from 'events';

const eventPrifix = 'videosdk-live-react-sdk';

const events = {
  'participant-joined': `${eventPrifix}-participant-joined`,
  'participant-left': `${eventPrifix}-participant-left`,
  'speaker-changed': `${eventPrifix}-speaker-changed`,
  'presenter-changed': `${eventPrifix}-presenter-changed`,
  'main-participant-changed': `${eventPrifix}-main-participant-changed`,
  'entry-requested': `${eventPrifix}-entry-requested`,
  'entry-responded': `${eventPrifix}-entry-responded`,
  'recording-started': `${eventPrifix}-recording-started`,
  'recording-stopped': `${eventPrifix}-recording-stopped`,
  data: `${eventPrifix}-data`,
  'meeting-joined': `${eventPrifix}-meeting-joined`,
  'meeting-left': `${eventPrifix}-meeting-left`,
  'live-stream-started': `${eventPrifix}-live-stream-started`,
  'live-stream-stopped': `${eventPrifix}-live-stream-stopped`,
  'webcam-requested': `${eventPrifix}-webcam-requested`,
  'mic-requested': `${eventPrifix}-mic-requested`,
  'pin-state-changed': `${eventPrifix}-pin-state-changed`,
  'connection-open': `${eventPrifix}-connection-open`,
  'connection-close': `${eventPrifix}-connection-close`,
  'switch-meeting': `${eventPrifix}-switch-meeting`,
  error: `${eventPrifix}-error`,
  'hls-started': `${eventPrifix}-hls-started`,
  'hls-stopped': `${eventPrifix}-hls-stopped`,
  'hls-state-changed': `${eventPrifix}-hls-state-changed`,
  'hls-playable-state-changed': `${eventPrifix}-hls-playable-state-changed`,
  'recording-state-changed': `${eventPrifix}-recording-state-changed`,
  'livestream-state-changed': `${eventPrifix}-livestream-state-changed`,
  'meeting-state-changed': `${eventPrifix}-meeting-state-changed`,
  'participant-mode-changed': `${eventPrifix}-participant-mode-changed`,
  'transcription-state-changed': `${eventPrifix}-transcription-state-changed`,
  'transcription-text': `${eventPrifix}-transcription-text`,
  'translation-state-changed': `${eventPrifix}-translation-state-changed`,
  'translation-language-changed': `${eventPrifix}-translation-language-changed`,
  'translation-text': `${eventPrifix}-translation-text`,
  'character-joined': `${eventPrifix}-character-joined`,
  'character-left': `${eventPrifix}-character-left`,
  'whiteboard-started': `${eventPrifix}-whiteboard-started`,
  'whiteboard-stopped': `${eventPrifix}-whiteboard-stopped`,
  'paused-all-streams': `${eventPrifix}-paused-all-streams`,
  'resumed-all-streams': `${eventPrifix}-resumed-all-streams`,
  'media-relay-started': `${eventPrifix}-media-relay-started`,
  'media-relay-stopped': `${eventPrifix}-media-relay-stopped`,
  'media-relay-error': `${eventPrifix}-media-relay-error`,
  'media-relay-request-response': `${eventPrifix}-media-relay-request-response`,
  'media-relay-request-received': `${eventPrifix}-media-relay-request-received`,
  'quality-limitation': `${eventPrifix}-quality-limitation`,
  'audio-input-silence': `${eventPrifix}-audio-input-silence`,
  'codec-changed': `${eventPrifix}-codec-changed`,
  // Per-participant events (suffixed with -${participantId} at emit time)
  'participant-stream-enabled': `${eventPrifix}-p-stream-enabled`,
  'participant-stream-disabled': `${eventPrifix}-p-stream-disabled`,
  'participant-media-status-changed': `${eventPrifix}-p-media-status-changed`,
  'participant-video-quality-changed': `${eventPrifix}-p-video-quality-changed`,
  'participant-stream-paused': `${eventPrifix}-p-stream-paused`,
  'participant-stream-resumed': `${eventPrifix}-p-stream-resumed`,
  'participant-producer-added': `${eventPrifix}-p-producer-added`,
  'participant-producer-removed': `${eventPrifix}-p-producer-removed`,
  'participant-consumer-added': `${eventPrifix}-p-consumer-added`,
  'participant-consumer-removed': `${eventPrifix}-p-consumer-removed`,
  // Agent-only per-participant events
  'agent-state-changed': `${eventPrifix}-p-agent-state-changed`,
  'agent-transcription-received': `${eventPrifix}-p-agent-transcription-received`,
  'agent-metrics': `${eventPrifix}-p-agent-metrics`,
  // Per-character events (suffixed with -${characterId} at emit time)
  'character-state-changed': `${eventPrifix}-c-state-changed`,
  'character-message': `${eventPrifix}-c-message`,
  'user-message': `${eventPrifix}-c-user-message`,
  'character-data': `${eventPrifix}-c-data`,
  'character-error': `${eventPrifix}-c-error`
};

const eventEmitter = new EventEmitter();
eventEmitter.setMaxListeners(9999);

function debounce(func, delay) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
}

const rejectNotJoined = method => {
  const err = new Error(
    `You must join the meeting before calling the ${method}() method.`
  );
  err.name = 'ERROR_ACTION_PERFORMED_BEFORE_MEETING_JOINED';
  err.code = 3035;

  eventEmitter.emit(events['error'], {
    name: err.name,
    code: err.code,
    message: err.message
  });

  return Promise.reject(err);
};

// Rejection for a method called on a subject (participant, stream, character)
// that is no longer in the meeting state — it left, or the meeting was never
// joined. Without this, `ref.current?.method()` returns `undefined` and callers
// doing `.catch()` on a documented Promise get a TypeError instead.
//
// Reuses 3035 rather than minting a wrapper-local code: error codes belong to
// the core SDK catalogue, and a new one here would collide the moment core
// claims the same number. The message carries the detail the code can't.
//
// Unlike rejectNotJoined, this does NOT emit on `onError`. The rejection has a
// call site and is fully self-describing, so emitting as well would report one
// failure twice.
const rejectUnavailable = (method, subject) => {
  const err = new Error(
    `The ${subject} is no longer available, so ${method}() could not be called.`
  );
  err.name = 'ERROR_ACTION_PERFORMED_BEFORE_MEETING_JOINED';
  err.code = 3035;

  return Promise.reject(err);
};

export { eventEmitter, events, debounce, rejectNotJoined, rejectUnavailable };
